/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Big } from 'big.js';

import { escapeRegex } from '../helper/escape-regex';
import { setupOptions } from '../helper/setup-options.helper';
import { CoreErrorKey } from '../key/core-error.key';
import { CoreFormatKey } from '../key/core-format.key';
import { CoreFormatterKey } from '../key/core-formatter.key';
import { ApplicationError } from '../model/application-error.model';
import { Formatter, FormatterProvider } from '../provider/formatter.provider';

export interface NumberFormatterOptions {
  nullFormat?: string;
  supportNaN?: boolean;
  prefix?: string;
  suffix?: string;
  positiveSign?: boolean;
  decimalDelimiter?: string;
  thousandsDelimiter?: string;
  minIntegerDigits?: number;
  minFractionDigits?: number;
  maxFractionDigits?: number;
  minimumWidth?: number;
  parseTo?: 'number' | 'string' | 'big';
}

export class NumberFormatter implements Formatter {
  private static readonly NAN_REGEX = /^\s*nan\s*$/i;
  private static readonly NUMBER_REGEX = /^\s*([+-])?\s*(\d+)?(?:\.(\d*[1-9])?[0]*)?\s*(e[-+][0-9]*)?\s*$/;

  protected readonly options: NumberFormatterOptions;
  private readonly formattedNumberRegex: RegExp;
  private readonly thousandsDelimiterRegex: RegExp;

  constructor(options?: NumberFormatterOptions) {
    this.options = NumberFormatter.setupOptions(options);
    const prefix = escapeRegex(this.options.prefix);
    const thousandsDelimiter = this.options.thousandsDelimiter ? escapeRegex(this.options.thousandsDelimiter) : '';
    const decimalDelimiter = escapeRegex(this.options.decimalDelimiter);
    const suffix = escapeRegex(this.options.suffix);
    this.formattedNumberRegex = new RegExp(
      `^\\s*(?:${prefix})?\\s*`                                             // prefix and whitespace
      + `([+-])?\\s*`                                                       // sign
      // + `(0*)([1-9]\\d?\\d?(?:(?:${thousandsDelimiter}\\d\\d\\d)*|\\d*))?`  // leading zeros and integer number
      + `(0*)((?:\\d|${thousandsDelimiter})*)`                              // leading zeros integer number
      + `(?:${decimalDelimiter}(\\d*[1-9])?0*)?`                            // decimal number and trailing zeros
      + `\\s*(?:${suffix})?\\s*$`                                           // suffix and whitespace
    );
    this.thousandsDelimiterRegex = this.options.thousandsDelimiter
      ? new RegExp(`[${thousandsDelimiter}]`, 'g')
      : null;
  }

  format(value: number | string | Big): string {
    if (value === undefined) {
      return '';
    }
    if (value === null) {
      return this.options.nullFormat;
    }
    if (typeof value !== 'string') {
      value = value.toString();
    }
    value = value.trim();
    if (value === '') {
      return this.options.nullFormat;
    }
    if (this.options.supportNaN && NumberFormatter.NAN_REGEX.test(value)) {
      return 'NaN';
    }
    let numberMatch = NumberFormatter.NUMBER_REGEX.exec(value);
    if (!numberMatch) {
      throw new ApplicationError({
        key: CoreErrorKey.FORMAT_ERROR,
        arguments: { formatterKey: CoreFormatterKey.NUMBER, options: this.options, value },
        message: `Format value error: Value '${value}' invalid (${CoreFormatterKey.NUMBER})`
      });
    }
    if (numberMatch[3] && numberMatch[3].length > this.options.maxFractionDigits) {
      numberMatch = NumberFormatter.NUMBER_REGEX.exec(new Big(value).toFixed(this.options.maxFractionDigits));
    }
    let formattedValue = '';
    if (numberMatch[1] === '-' || this.options.positiveSign) {
      formattedValue += numberMatch[1] || '+';
    }
    const integerString = numberMatch[2] || '0';
    const leadingZerosCount = Math.max(this.options.minIntegerDigits - integerString.length, 0);
    const integerDigitsCount = integerString.length + leadingZerosCount;
    const thousandsDelimiterRest = integerDigitsCount % 3;
    for (let i = 0; i < integerDigitsCount; i++) {
      if (i % 3 === thousandsDelimiterRest && i > 0) {
        formattedValue += this.options.thousandsDelimiter;
      }
      formattedValue += i < leadingZerosCount ? '0' : integerString[i - leadingZerosCount];
    }
    const fractionString = numberMatch[3] || '';
    const trailingZerosCount = Math.max(this.options.minFractionDigits - fractionString.length, 0);
    const trailingDigitsCount = fractionString.length + trailingZerosCount;
    if (trailingDigitsCount > 0) {
      formattedValue += this.options.decimalDelimiter;
      formattedValue += fractionString;
      for (let i = 0; i < trailingZerosCount; i++) {
        formattedValue += '0';
      }
    }
    const prefix = this.options.prefix ? this.options.prefix + ' ' : '';
    const suffix = this.options.suffix ? ' ' + this.options.suffix : '';
    const leadingSpaceCount = Math.max(
      this.options.minimumWidth - formattedValue.length - prefix.length - suffix.length, 0
    );
    if (leadingSpaceCount > 0) {
      let leadingSpaces = '';
      for (let i = 0; i < leadingSpaceCount; i++) {
        leadingSpaces += ' ';
      }
      formattedValue = leadingSpaces + formattedValue;
    }
    return prefix + formattedValue + suffix;
  }

  parse(value: string): number | string | Big {
    if (value === undefined) {
      return undefined;
    }
    if (value === null) {
      return null;
    }
    value = value.trim();
    if (!value || value === this.options.nullFormat) {
      return null;
    }
    if (this.options.supportNaN && NumberFormatter.NAN_REGEX.test(value)) {
      switch (this.options.parseTo) {
        case 'number':
          return Number.NaN;
        case 'string':
          return 'NaN';
        case 'big':
          // big.js does not support NaN
          throw new ApplicationError({
            key: CoreErrorKey.PARSE_ERROR,
            arguments: { formatterKey: CoreFormatterKey.NUMBER, options: this.options, value },
            message: `Parse value error: Value '${value}' invalid (${CoreFormatterKey.NUMBER})`
          });
      }
      throw new ApplicationError({
        key: CoreErrorKey.INVALID_STATE,
        message: `Invalid state`
      });
    }
    const SIGN = 1;
    const LEADING_ZEROS = 2;
    const INTEGER = 3;
    const DECIMAL = 4;
    const numberMatch = this.formattedNumberRegex.exec(value);
    if (!numberMatch || (numberMatch[SIGN]
      && !numberMatch[LEADING_ZEROS]
      && !numberMatch[INTEGER]
      && !numberMatch[DECIMAL]
    )) {
      throw new ApplicationError({
        key: CoreErrorKey.PARSE_ERROR,
        arguments: { formatterKey: CoreFormatterKey.NUMBER, options: this.options, value },
        message: `Parse value error: Value '${value}' invalid (${CoreFormatterKey.NUMBER})`
      });
    }
    const formattedSign = numberMatch[SIGN] === '-' ? '-' : '';
    const formattedInteger = numberMatch[INTEGER]
      ? (this.thousandsDelimiterRegex
        ? numberMatch[INTEGER].replace(this.thousandsDelimiterRegex, '') || '0'
        : numberMatch[INTEGER])
      : '0';
    const formattedDecimal = numberMatch[DECIMAL]
      ? `.${numberMatch[DECIMAL]}`
      : '';
    const formattedNumber = formattedSign + formattedInteger + formattedDecimal;
    switch (this.options.parseTo) {
      case 'number':
        return parseFloat(formattedNumber);
      case 'string':
        return formattedNumber;
      case 'big':
        return new Big(formattedNumber);
    }
    throw new ApplicationError({
      key: CoreErrorKey.INVALID_STATE,
      message: `Invalid state`
    });
  }

  clone(options: NumberFormatterOptions): NumberFormatter {
    return new NumberFormatter(Object.assign({}, this.options, options));
  }

  static setupOptions(options: NumberFormatterOptions): NumberFormatterOptions {
    try {
      options = setupOptions({
        nullFormat: '-',
        supportNaN: false,
        prefix: '',
        suffix: '',
        positiveSign: false,
        decimalDelimiter: '.',
        thousandsDelimiter: ' ',
        minimumWidth: 0,
        minIntegerDigits: 0,
        minFractionDigits: 0,
        maxFractionDigits: 20,
        parseTo: { default: 'number', values: ['number', 'string', 'big'] }
      }, options);
      if (!options.decimalDelimiter) {
        throw new ApplicationError({
          key: CoreErrorKey.OPTION_INVALID,
          arguments: { key: 'decimalDelimiter', value: options.decimalDelimiter },
          message: `Formatter option 'decimalDelimiter' invalid`
        });
      }
      return options;
    } catch (error) {
      throw new ApplicationError({
        key: CoreErrorKey.FORMATTER_OPTIONS_INVALID,
        arguments: { formatterKey: CoreFormatterKey.NUMBER, options, error },
        message: `Formatter options for '${CoreFormatterKey.NUMBER}' invalid`
      });
    }
  }

}

export function formatNumber(value: number | string | Big, options?: NumberFormatterOptions): string {
  const formatter = FormatterProvider.resolveFormatter(CoreFormatKey.NUMBER, options) || new NumberFormatter(options);
  return formatter.format(value);
}

export function parseNumber(value: string, options?: NumberFormatterOptions): string | number | Big {
  const formatter = FormatterProvider.resolveFormatter(CoreFormatKey.NUMBER, options) || new NumberFormatter(options);
  return formatter.parse(value);
}
